import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { todayISO } from "@/lib/localDate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PlusIcon, TrashIcon, ConfettiIcon } from "@phosphor-icons/react";

/*
  Company public holidays (public.public_holidays — schema_public_holidays.sql).

  Everyone sees the list; these days are off for the whole company
  automatically, so nobody applies leave for them, and leave day counts skip
  them. Only an admin gets the add/remove controls — and RLS refuses anyone
  else regardless of what this renders.

  Dates are YYYY-MM-DD strings and are formatted by splitting, never through
  `new Date("yyyy-mm-dd")` (UTC midnight — reads back a day early east of
  Greenwich; see lib/localDate.js).
*/
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const fmtHolidayDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return `${DOW[new Date(y, m - 1, d).getDay()]}, ${d} ${MONTH_SHORT[m - 1]} ${y}`;
};

export default function HolidaysPanel({ holidays, isAdmin, onChanged }) {
  const today = todayISO();
  const [showPast, setShowPast] = useState(false);
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const visible = holidays.filter((h) => showPast || h.holiday_date >= today);
  const pastCount = holidays.length - holidays.filter((h) => h.holiday_date >= today).length;

  const add = async () => {
    if (!date || !name.trim()) { toast.error("Enter a date and a name"); return; }
    if (holidays.some((h) => h.holiday_date === date)) { toast.error("That date is already a holiday"); return; }
    setSaving(true);
    try {
      await api.post("/holidays", { holiday_date: date, name: name.trim() });
      toast.success("Holiday added");
      setDate(""); setName("");
      await onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSaving(false); }
  };

  const remove = async (h) => {
    if (!confirm(`Remove ${h.name} (${fmtHolidayDate(h.holiday_date)}) from the holiday list?`)) return;
    try {
      await api.delete(`/holidays/${h._id}`);
      toast.success("Holiday removed");
      await onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  return (
    <div className="border border-border rounded-sm bg-white" data-testid="holidays-panel">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <ConfettiIcon size={18} weight="fill" className="text-amber-600" /> Public holidays
          </h2>
          <p className="text-xs text-zinc-500">Company-wide days off — no need to apply. They aren't counted in leave days.</p>
        </div>
        {pastCount > 0 && (
          <button
            onClick={() => setShowPast((v) => !v)}
            data-testid="holidays-toggle-past"
            className="px-2.5 py-1.5 text-xs font-medium border border-border rounded-sm hover:bg-zinc-50 text-zinc-600 whitespace-nowrap"
          >
            {showPast ? "Hide past" : `Show past (${pastCount})`}
          </button>
        )}
      </div>

      {isAdmin && (
        <div className="px-5 py-3 border-b border-border bg-zinc-50/60 flex flex-wrap items-center gap-2" data-testid="holidays-add-form">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm h-9 w-44" data-testid="holiday-date-input" />
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Holiday name, e.g. Diwali"
                 className="rounded-sm h-9 flex-1 min-w-[180px]" data-testid="holiday-name-input"
                 onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
          <Button onClick={add} disabled={saving} data-testid="holiday-add-button" className="rounded-sm h-9 bg-[#0176D3] hover:bg-[#005FB2]">
            <PlusIcon size={13} weight="bold" className="mr-1" /> {saving ? "Adding…" : "Add holiday"}
          </Button>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="p-6 text-center text-sm text-zinc-500">
          {holidays.length === 0 ? "No public holidays have been added yet." : "No upcoming public holidays."}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {visible.map((h) => {
            const past = h.holiday_date < today;
            return (
              <div key={h._id} data-testid={`holiday-${h.holiday_date}`}
                   className={`px-5 py-2.5 flex items-center justify-between gap-3 text-sm ${past ? "text-zinc-400" : ""}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-xs w-32 shrink-0">{fmtHolidayDate(h.holiday_date)}</span>
                  <span className={`truncate ${past ? "" : "font-medium text-[#0F172A]"}`}>{h.name}</span>
                </div>
                {isAdmin && (
                  <button onClick={() => remove(h)} title="Remove holiday" data-testid={`holiday-remove-${h.holiday_date}`}
                          className="p-1 border border-border rounded-sm hover:bg-red-50 hover:text-red-700 text-zinc-500">
                    <TrashIcon size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
